import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import testService from '../../services/testService';
import api from '../../services/api';
import { EXAM_MODES, TEST_TYPES, TEST_TYPE_OPTIONS, STUDENT_TOPIC_PERFORMANCE_SUMMARY } from '../../config/Config';
import { filterPublished } from '../../utils/publishedFilter';
import {
  testDialogFieldLabelSx,
  testDialogFieldShellSx,
  testDialogTopicChipSx,
} from './testDialogFieldStyles';
import { formatTeacherStudentOptionLabel } from '../../utils/teacherStudentRoster';
import TagSelector from '../forms/TagSelector';
import { processTagPayload } from '../../utils/tagPayload';

const AutoTestDialog = ({
  open,
  onClose,
  onSave,
  subjects = [],
  years = [],
  boards = [],
  topics = [],
  initialForm = null,
  showMyQuestions = true,
  title = "Auto-Generate Test",
  saveButtonText = "Generate",
  user = null,
  isFromAssignment = false,
  students = [],
  studentsLoading = false,
  initialSelectedStudents = []
}) => {
  const defaultForm = {
    title: '',
    subject: '',
    topics: [],
    test_type: TEST_TYPES.QUIZ,
    board: '',
    academic_year: '',
    num_questions: 10,
    duration_minutes: 60,
    percent_basic: 50,
    percent_advanced: 50,
    note: '',
    tags: [],
    mix_topics: false,
  };

  const [form, setForm] = useState(() => {
    // Initialize with initialForm if provided, otherwise use defaultForm
    if (initialForm?.test_type === TEST_TYPES.REVISE) {
      return {
        ...defaultForm,
        ...initialForm,
        test_type: TEST_TYPES.REVISE,
        num_questions: initialForm.num_questions || 10,
        duration_minutes: initialForm.duration_minutes || 60,
        percent_basic: initialForm.percent_basic || 50,
        percent_advanced: initialForm.percent_advanced || 50,
      };
    } else if (initialForm) {
      return { ...defaultForm, ...initialForm };
    }
    return defaultForm;
  });
  /** Off by default so topic list matches full curriculum bank (reports); teachers opt in to restrict. */
  const [myQuestions, setMyQuestions] = useState(false);
  const [isFirstOpen, setIsFirstOpen] = useState(true);
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);

  const [questionCounts, setQuestionCounts] = useState({
    total: 0,
    basic: 0,
    intermediate: 0,
    advanced: 0
  });
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [examModes, setExamModes] = useState([]);
  const [testMode, setTestMode] = useState('practice');
  const [localError, setLocalError] = useState('');
  const [showLocalSnackbar, setShowLocalSnackbar] = useState(false);
  const [topicPerformance, setTopicPerformance] = useState([]);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(
    initialSelectedStudents && initialSelectedStudents.length > 0 ? initialSelectedStudents[0] : null
  );

  // Filter test type options based on whether dialog is launched from assignment page
  const filteredTestTypeOptions = isFromAssignment 
    ? TEST_TYPE_OPTIONS 
    : TEST_TYPE_OPTIONS.filter(option => option.value !== TEST_TYPES.REVISE);

  // Show only published boards and academic years (case-insensitive)
  const publishedBoards = useMemo(() => filterPublished(boards), [boards]);
  const publishedYears = useMemo(() => filterPublished(years), [years]);



  const prevOpenRef = useRef(false);

  // Handle dialog open/close and fetch exam modes
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (open && isFirstOpen && justOpened) {
      setIsFirstOpen(false);

      // Reset form to initialForm when dialog opens
      if (initialForm) {
        if (initialForm?.test_type === TEST_TYPES.REVISE) {
          setForm({
            ...defaultForm,
            ...initialForm,
            test_type: TEST_TYPES.REVISE,
            num_questions: initialForm.num_questions || 10,
            duration_minutes: initialForm.duration_minutes || 60,
            percent_basic: initialForm.percent_basic || 50,
            percent_advanced: initialForm.percent_advanced || 50,
          });
        } else {
          setForm({ ...defaultForm, ...initialForm });
        }
      } else {
        setForm(defaultForm);
      }

      // Pre-fill selected student if provided (only when dialog first opens)
      if (initialSelectedStudents && initialSelectedStudents.length > 0) {
        setSelectedStudent(initialSelectedStudents[0]);
      } else {
        setSelectedStudent(null);
      }

      // Set default test mode to practice if not already set (but allow user to change it)
      setTestMode((prev) => (!prev || prev === '' ? 'practice' : prev));

      (async () => {
        try {
          const res = await api.get(EXAM_MODES + '?page_size=100');
          setExamModes(res.data.results || res.data || []);
        } catch {
          setExamModes([]);
        }
      })();
    } else if (!open) {
      // Reset all state when dialog closes
      setIsFirstOpen(true);
      setMyQuestions(false);
      setTopicPerformance([]);
      setSelectedStudent(null);
      setLoadingPerformance(false);
      setSubmitting(false);
      setForm(defaultForm);
    }
  }, [open, isFirstOpen]);
  
  // Note: selectedStudent initialization is handled in the main dialog open/close useEffect above
  // This separate effect was causing issues by resetting user selections, so it's been removed

  // Update form title when user prop or selected student becomes available for revision tests
  useEffect(() => {
    if (form.test_type !== TEST_TYPES.REVISE) return;
    const studentForTitle = selectedStudent || user;
    if (!studentForTitle || !formatTeacherStudentOptionLabel(studentForTitle)) return;
    const studentName = formatTeacherStudentOptionLabel(studentForTitle);
    const titleText = `Revision Test for ${studentName}`;
    setForm((prev) => {
      if (prev.title === titleText) return prev;
      return { ...prev, title: titleText };
    });
  }, [user, form.test_type, selectedStudent]);

  // Fetch student topic performance for revision tests
  // Use selected student if available, otherwise use user prop
  useEffect(() => {
    const fetchTopicPerformance = async () => {
      // Determine which student to use for performance data
      const studentForPerformance = selectedStudent || user;
      
      if (open && form.test_type === TEST_TYPES.REVISE && studentForPerformance) {
        // Get student ID - handle different ID formats
        // For normalized students from StudentAssignmentsPage, the id field contains the student user ID
        const getStudentId = (s) => {
          if (!s) return null;
          // Skip 'all' option
          if (s.id === 'all') return null;
          
          // For normalized students, id is the canonical student user ID
          if (s.id && typeof s.id === 'number') {
            return s.id;
          }
          // Try student field (the actual student user ID from relationships)
          if (s.student && typeof s.student === 'number') {
            return s.student;
          }
          // Fallback to other ID fields
          if (s.student_id && typeof s.student_id === 'number') {
            return s.student_id;
          }
          if (s.user && s.user.id) {
            return s.user.id;
          }
          return null;
        };
        
        const studentId = getStudentId(studentForPerformance);
        
        if (!studentId) {
          console.log('No valid student ID found:', studentForPerformance);
          setTopicPerformance([]);
          setLoadingPerformance(false);
          return;
        }
        
        setLoadingPerformance(true);
        try {
          console.log('Fetching performance for student ID:', studentId);
          const response = await api.get(`${STUDENT_TOPIC_PERFORMANCE_SUMMARY}?student_id=${studentId}`);
          console.log('Performance API response:', response.data);
          const performanceData = Array.isArray(response.data) ? response.data : [];
          
          // Filter topics that match the selected subject if subject is selected
          const filteredData = form.subject 
            ? performanceData.filter(item => {
                // Match topic with selected subject
                const matchingTopic = topics.find(t => 
                  t.id === item.topic_id && 
                  (String(t.subject) === String(form.subject) || String(t.subject_id) === String(form.subject))
                );
                return matchingTopic !== undefined;
              })
            : performanceData;
          
          console.log('Filtered performance data:', filteredData);
          setTopicPerformance(filteredData);
        } catch (error) {
          console.error('Error fetching topic performance:', error);
          console.error('Error details:', error.response?.data || error.message);
          setTopicPerformance([]);
        } finally {
          setLoadingPerformance(false);
        }
      } else {
        setTopicPerformance([]);
        setLoadingPerformance(false);
      }
    };

    fetchTopicPerformance();
  }, [open, form.test_type, form.subject, user, topics, selectedStudent]);

  // Topics with question counts for the bank — same filters as ManualTestDialog (incl. subject + mine + exam mode)
  useEffect(() => {
    const fetchFilteredTopics = async () => {
      if (!form.board || !form.academic_year || !form.subject) {
        setFilteredTopics([]);
        return;
      }

      setLoadingTopics(true);
      try {
        const fetchedTopics = await testService.fetchCurriculumSubjectTopics({
          subject: form.subject,
          board: form.board,
          academicYear: form.academic_year,
          examMode: testMode && testMode !== 'practice' ? testMode : 'practice',
          mineOnly: showMyQuestions && myQuestions,
        });
        setFilteredTopics(fetchedTopics);
      } catch (error) {
        console.error('Error fetching filtered topics:', error);
        setFilteredTopics([]);
      } finally {
        setLoadingTopics(false);
      }
    };

    fetchFilteredTopics();
  }, [form.board, form.academic_year, form.subject, testMode, showMyQuestions, myQuestions]);

  const topicsWithAvailableQuestions = useMemo(
    () => filteredTopics.filter((t) => (t.question_count ?? 0) > 0),
    [filteredTopics]
  );

  /** Drop topic chips that have no matching questions under current filters. */
  useEffect(() => {
    if (loadingTopics) return;
    const allowed = new Set(topicsWithAvailableQuestions.map((t) => String(t.id)));
    setForm((prev) => {
      if (!prev.topics?.length) return prev;
      const kept = prev.topics.filter((topic) => {
        const id = String(typeof topic === 'object' ? topic.id : topic);
        return allowed.has(id);
      });
      if (kept.length === prev.topics.length) return prev;
      return { ...prev, topics: kept };
    });
  }, [topicsWithAvailableQuestions, loadingTopics]);

  // Fetch question counts when criteria changes
  useEffect(() => {
    const fetchQuestionCounts = async () => {
      // Only fetch if we have the minimum required criteria
      if (form.subject && form.board && form.academic_year && form.topics && form.topics.length > 0) {
        setIsLoadingCounts(true);
        try {
          const criteria = {
            subject: form.subject,
            board: form.board,
            academic_year: form.academic_year,
            topics: form.topics.map(topic => typeof topic === 'object' ? topic.id : topic),
            // For teachers: use myQuestions toggle value, for students: always false (get all questions)
            mine_only: showMyQuestions ? myQuestions : false,
            exam_mode: testMode
          };
          
          const counts = await testService.getQuestionCounts(criteria);
          setQuestionCounts(counts);
        } catch (error) {
    
          // Reset counts on error
          setQuestionCounts({ total: 0, basic: 0, intermediate: 0, advanced: 0 });
        } finally {
          setIsLoadingCounts(false);
        }
      } else {
        // Reset counts if criteria are incomplete
        setQuestionCounts({ total: 0, basic: 0, intermediate: 0, advanced: 0 });
      }
    };

    // Debounce the API call to avoid too many requests
    const timeoutId = setTimeout(fetchQuestionCounts, 500);
    return () => clearTimeout(timeoutId);
  }, [form.subject, form.board, form.academic_year, form.topics, myQuestions, showMyQuestions, testMode]);

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setForm(prev => {
      if (field === 'subject') {
        // Clear topics when subject changes
        return { ...prev, subject: value, topics: [] };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleMyQuestionsChange = (event) => {
    setMyQuestions(event.target.checked);
    setFilteredTopics([]);
    setQuestionCounts({ total: 0, basic: 0, intermediate: 0, advanced: 0 });
    setForm((prev) => ({ ...prev, topics: [] }));
    setLocalError('');
  };

  const handlePercentChange = (field) => (e) => {
    const value = parseInt(e.target.value) || 0;
    setForm(prev => {
      const otherField = field === 'percent_basic' ? 'percent_advanced' : 'percent_basic';
      const newOtherValue = Math.max(0, 100 - value);
      
      return {
        ...prev,
        [field]: value,
        [otherField]: newOtherValue
      };
    });
  };

  const validateForm = () => {
    if (!form.title.trim()) return 'Please enter a test title';
    if (!form.test_type) return 'Please select a test type';
    if (!form.subject) return 'Please select a subject';
    if (!form.board) return 'Please select a board';
    if (!form.academic_year) return 'Please select an academic year';
    if (!form.topics || form.topics.length === 0) return 'Please select at least one topic';
    if (form.num_questions <= 0) return 'Number of questions must be greater than 0';
    if (form.num_questions > 50) return 'Number of questions cannot exceed 50';
    if (form.duration_minutes <= 0) return 'Duration must be greater than 0';
    if (form.duration_minutes > 480) return 'Duration cannot exceed 8 hours (480 minutes)';
    
    // Validate student selection when dialog is opened from assignment page
    if (isFromAssignment && !selectedStudent) {
      return 'Please select a student';
    }
    
    // Validate percentage fields
    const totalPct = form.percent_basic + form.percent_advanced;
    if (totalPct !== 100) {
      return `Basic and Advanced percentages must sum to 100% (currently ${totalPct}%)`;
    }
    
    // Validate against available questions (only if we have counts)
    if (questionCounts.total > 0) {
      if (form.num_questions > questionCounts.total) {
        return `Only ${questionCounts.total} questions available, but you requested ${form.num_questions}`;
      }
      
      // Validate basic questions availability
      const requestedBasic = Math.round((form.percent_basic / 100) * form.num_questions);
      if (requestedBasic > 0) {
        if (questionCounts.basic === 0) {
          return `No Basic questions available for the selected criteria, but you requested ${requestedBasic}`;
        }
        if (requestedBasic > questionCounts.basic) {
          return `Only ${questionCounts.basic} Basic questions available, but you requested ${requestedBasic}`;
        }
      }
      
      // Validate advanced questions availability
      const requestedAdvanced = Math.round((form.percent_advanced / 100) * form.num_questions);
      if (requestedAdvanced > 0) {
        if (questionCounts.advanced === 0) {
          return `No Advanced questions available for the selected criteria, but you requested ${requestedAdvanced}`;
        }
        if (requestedAdvanced > questionCounts.advanced) {
          return `Only ${questionCounts.advanced} Advanced questions available, but you requested ${requestedAdvanced}`;
        }
      }
    }
    
    return null;
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      setLocalError(error);
      setShowLocalSnackbar(true);
      return;
    }

    // Prevent multiple submissions
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setLocalError('');

    try {
      // Extract topic IDs from topic objects
      const topicIds = form.topics.map(topic => typeof topic === 'object' ? topic.id : topic);
      const tagIds = await processTagPayload(form.tags || []);

      const payload = {
        title: (form.title || '').trim(),
        subject: form.subject ? Number(form.subject) : null,
        board: form.board ? Number(form.board) : null,
        academic_year: form.academic_year ? Number(form.academic_year) : null,
        topics: topicIds.map(id => Number(id)),
        test_type: form.test_type,
        num_questions: Number(form.num_questions || 0),
        duration_minutes: Number(form.duration_minutes || 0),
        percent_basic: Number(form.percent_basic || 0),
        percent_advanced: Number(form.percent_advanced || 0),
        note: form.note || '',
        my_questions: showMyQuestions ? myQuestions : false,
        mix_topics: showMyQuestions ? Boolean(form.mix_topics) : false,
        tags: tagIds,
      };

      // Call the parent's onSave with the payload and selected student
      const result = await onSave({ 
        ...payload, 
        exam_mode: (testMode === 'practice' ? null : Number(testMode)),
        selectedStudent: isFromAssignment ? selectedStudent : undefined
      });
      
      // If the parent returns an error, display it locally
      if (result && result.error) {
        setLocalError(result.error);
        setShowLocalSnackbar(true);
      }
    } catch (err) {
      setLocalError(err.message || 'An error occurred while creating the test');
      setShowLocalSnackbar(true);
    } finally {
      setSubmitting(false);
    }
  };

  const canGenerate = (
    !!form.subject &&
    !!form.board &&
    !!form.academic_year &&
    Array.isArray(form.topics) && form.topics.length > 0 &&
    (questionCounts.total || 0) > 0
  );

  const handleDialogClose = (event, reason) => {
    if (submitting || reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          width: 'min(1000px, 96vw)',
          maxWidth: 'min(1000px, 96vw) !important',
          maxHeight: 'min(92vh, 920px)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ pr: 2, flexShrink: 0 }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'auto',
          p: { xs: 2, sm: 2.75 },
          bgcolor: (t) => (t.palette.mode === 'light' ? 'grey.50' : 'background.default'),
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Grid container spacing={2.25}>
            {isFromAssignment && (
              <Grid item xs={12}>
                <Stack spacing={0.75}>
                  <Typography sx={testDialogFieldLabelSx}>Select student</Typography>
                  <Autocomplete
                    options={students}
                    loading={studentsLoading}
                    getOptionLabel={(option) => formatTeacherStudentOptionLabel(option)}
                    value={selectedStudent}
                    onChange={(e, newValue) => setSelectedStudent(newValue)}
                    disabled={submitting}
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false;
                      return (
                        String(option.id || option.student_id || option.student || '') ===
                          String(value.id || value.student_id || value.student || '') ||
                        String(option.email || '') === String(value.email || '') ||
                        (option.managed_student_id != null &&
                          String(option.managed_student_id) === String(value.managed_student_id))
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        hiddenLabel
                        placeholder="Choose a student to assign this test to"
                        variant="outlined"
                        fullWidth
                        size="small"
                        sx={testDialogFieldShellSx}
                        helperText={
                          !studentsLoading && students.length === 0
                            ? 'No students linked to your account yet.'
                            : undefined
                        }
                      />
                    )}
                    renderOption={(props, option) => {
                      const optionKey =
                        option.row_key ||
                        option.id ||
                        option.student_id ||
                        option.student ||
                        option.managed_student_id ||
                        option.email ||
                        'unknown';
                      const email = (option.email || '').trim();
                      const displayName = (option.display_name || '').trim();
                      const name = (option.name || '').trim();
                      const primary = email || displayName || name || 'Student';
                      const subtitle =
                        email && (displayName || name) && (displayName || name) !== email
                          ? displayName || name
                          : null;
                      return (
                        <li {...props} key={optionKey}>
                          <Box>
                            <Typography variant="body1">
                              {primary}
                            </Typography>
                            {subtitle && (
                              <Typography variant="caption" color="text.secondary">
                                {subtitle}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      );
                    }}
                    size="small"
                  />
                </Stack>
              </Grid>
            )}

            {showMyQuestions && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 0.75, sm: 2 },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={myQuestions}
                        onChange={handleMyQuestionsChange}
                        color="primary"
                        disabled={submitting}
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={600}>
                        Use my questions only
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 760 }}>
                    On: Your questions only. Off: Full curriculum bank.
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Row 1: Title, duration, test type, test mode */}
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="auto-test-title" sx={testDialogFieldLabelSx}>
                  Test title *
                </Typography>
                <TextField
                  id="auto-test-title"
                  fullWidth
                  hiddenLabel
                  size="small"
                  placeholder="e.g. End of Term Mathematics Assessment"
                  value={form.title}
                  onChange={handleInputChange('title')}
                  disabled={submitting}
                  sx={testDialogFieldShellSx}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="auto-test-duration" sx={testDialogFieldLabelSx}>
                  Duration (min)
                </Typography>
                <TextField
                  id="auto-test-duration"
                  select
                  fullWidth
                  hiddenLabel
                  size="small"
                  value={form.duration_minutes}
                  onChange={handleInputChange('duration_minutes')}
                  disabled={submitting}
                  sx={testDialogFieldShellSx}
                >
                  {[15, 30, 45, 60, 90, 120].map((minutes) => (
                    <MenuItem key={minutes} value={minutes}>
                      {minutes} min
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="auto-test-type" sx={testDialogFieldLabelSx}>
                  Test type *
                </Typography>
                <TextField
                  id="auto-test-type"
                  select
                  fullWidth
                  hiddenLabel
                  size="small"
                  value={form.test_type}
                  onChange={handleInputChange('test_type')}
                  disabled={submitting || form.test_type === TEST_TYPES.REVISE}
                  sx={testDialogFieldShellSx}
                >
                  {filteredTestTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="auto-test-mode" sx={testDialogFieldLabelSx}>
                  Test mode *
                </Typography>
                <TextField
                  id="auto-test-mode"
                  select
                  fullWidth
                  hiddenLabel
                  size="small"
                  required
                  value={testMode}
                  onChange={(e) => {
                    setTestMode(e.target.value);
                    setForm((prev) => ({ ...prev, topics: [] }));
                  }}
                  disabled={submitting}
                  sx={testDialogFieldShellSx}
                >
                  <MenuItem value="practice">Practice</MenuItem>
                  {examModes
                    .filter((m) => String(m.mode).toLowerCase() !== 'practice')
                    .map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.mode}
                      </MenuItem>
                    ))}
                </TextField>
              </Stack>
            </Grid>

            {/* Row 2: Board, academic year, subject, topics */}
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="auto-test-board" sx={testDialogFieldLabelSx}>
                  Board
                </Typography>
                <TextField
                  id="auto-test-board"
                  select
                  fullWidth
                  hiddenLabel
                  size="small"
                  value={form.board}
                  onChange={(e) => {
                    handleInputChange('board')(e);
                    setForm((prev) => ({ ...prev, topics: [] }));
                  }}
                  disabled={submitting}
                  sx={testDialogFieldShellSx}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">
                    <em>Select board</em>
                  </MenuItem>
                  {publishedBoards.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="auto-test-year" sx={testDialogFieldLabelSx}>
                  Academic year
                </Typography>
                <TextField
                  id="auto-test-year"
                  select
                  fullWidth
                  hiddenLabel
                  size="small"
                  value={form.academic_year}
                  onChange={(e) => {
                    handleInputChange('academic_year')(e);
                    setForm((prev) => ({ ...prev, topics: [] }));
                  }}
                  disabled={submitting}
                  sx={testDialogFieldShellSx}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">
                    <em>Select year</em>
                  </MenuItem>
                  {publishedYears.map((y) => (
                    <MenuItem key={y.id} value={y.id}>
                      {y.label || y.year}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="auto-test-subject" sx={testDialogFieldLabelSx}>
                  Subject
                </Typography>
                <TextField
                  id="auto-test-subject"
                  select
                  fullWidth
                  hiddenLabel
                  size="small"
                  value={form.subject}
                  onChange={handleInputChange('subject')}
                  disabled={submitting}
                  sx={testDialogFieldShellSx}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">
                    <em>Select subject</em>
                  </MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography sx={testDialogFieldLabelSx}>Topics</Typography>
                <Autocomplete
                  multiple
                  disabled={submitting || loadingTopics || !form.board || !form.academic_year || !form.subject}
                  options={topicsWithAvailableQuestions}
                  getOptionLabel={(option) => {
                    const topicName = option.topic || option.name || '';
                    const questionCount = option.question_count ?? 0;
                    return `${topicName} (${questionCount} Qs)`;
                  }}
                  value={form.topics.filter((topic) => {
                    const id = String(typeof topic === 'object' ? topic.id : topic);
                    return topicsWithAvailableQuestions.some((t) => String(t.id) === id);
                  })}
                  onChange={(e, newValue) => setForm((prev) => ({ ...prev, topics: newValue }))}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return String(option.id) === String(value.id || value);
                  }}
                  ListboxProps={{
                    'data-testid': 'auto-test-topic-listbox',
                    style: { maxHeight: 320, overflowY: 'auto' },
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const topicName = option.topic || option.name || '';
                      const questionCount = option.question_count ?? 0;
                      const label = `${topicName} (${questionCount} Qs)`;
                      return (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={label}
                          size="small"
                          sx={testDialogTopicChipSx}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      hiddenLabel
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="Type to add topics…"
                      sx={testDialogFieldShellSx}
                      helperText={
                        !form.board || !form.academic_year || !form.subject
                          ? 'Select board, academic year, and subject first.'
                          : loadingTopics
                            ? 'Loading topics…'
                            : topicsWithAvailableQuestions.length === 0
                              ? 'No topics have questions for the current filters. Adjust board, year, subject, test mode, or turn off "Use my questions only" to use the full bank.'
                              : undefined
                      }
                      FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const topicName = option.topic || option.name || '';
                    const questionCount = option.question_count ?? 0;
                    const displayText = `${topicName} (${questionCount} Qs)`;
                    return (
                      <li {...props} key={option.id}>
                        {displayText}
                      </li>
                    );
                  }}
                  size="small"
                />
              </Stack>
            </Grid>

            {/* Row 3: % basic, % advanced, number of questions */}
            <Grid item xs={12} sm={6} md={4}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="auto-pct-basic" sx={testDialogFieldLabelSx}>
                  % Basic questions
                </Typography>
                <TextField
                  id="auto-pct-basic"
                  type="number"
                  fullWidth
                  hiddenLabel
                  size="small"
                  inputProps={{ min: 0, max: 100 }}
                  value={form.percent_basic}
                  onChange={handlePercentChange('percent_basic')}
                  disabled={submitting}
                  sx={testDialogFieldShellSx}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="auto-pct-advanced" sx={testDialogFieldLabelSx}>
                  % Advanced questions
                </Typography>
                <TextField
                  id="auto-pct-advanced"
                  type="number"
                  fullWidth
                  hiddenLabel
                  size="small"
                  inputProps={{ min: 0, max: 100 }}
                  value={form.percent_advanced}
                  onChange={handlePercentChange('percent_advanced')}
                  disabled={submitting}
                  sx={testDialogFieldShellSx}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="auto-num-questions" sx={testDialogFieldLabelSx}>
                  Number of questions
                </Typography>
                <TextField
                  id="auto-num-questions"
                  select
                  fullWidth
                  hiddenLabel
                  size="small"
                  value={form.num_questions}
                  onChange={handleInputChange('num_questions')}
                  disabled={submitting}
                  sx={testDialogFieldShellSx}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={40}>40</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </TextField>
              </Stack>
            </Grid>

            {showMyQuestions && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={(
                      <Switch
                        checked={Boolean(form.mix_topics)}
                        onChange={(event) => setForm((prev) => ({
                          ...prev,
                          mix_topics: event.target.checked,
                        }))}
                        disabled={submitting}
                      />
                    )}
                    label="Mix topics"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Shuffle and avoid repeats.
                  </Typography>
                </Stack>
              </Grid>
            )}

            {!(isFromAssignment && form.test_type !== TEST_TYPES.REVISE) && (
              <Grid item xs={12}>
                <Stack spacing={0.75}>
                  <Typography component="label" htmlFor="auto-test-note" sx={testDialogFieldLabelSx}>
                    Note
                  </Typography>
                  <TextField
                    id="auto-test-note"
                    fullWidth
                    hiddenLabel
                    multiline
                    minRows={3}
                    placeholder="Optional note for this test…"
                    value={form.note}
                    onChange={handleInputChange('note')}
                    disabled={submitting || form.test_type === TEST_TYPES.REVISE}
                    sx={testDialogFieldShellSx}
                  />
                </Stack>
              </Grid>
            )}

            <Grid item xs={12}>
              <Stack spacing={0.75}>
                <Typography sx={testDialogFieldLabelSx}>Tags</Typography>
                <TagSelector
                  value={form.tags || []}
                  onChange={(tags) => setForm((prev) => ({ ...prev, tags }))}
                  label=""
                  helperText="Add descriptive tags (optional)"
                  disabled={submitting}
                  size="small"
                />
              </Stack>
            </Grid>

            {(form.subject &&
              form.board &&
              form.academic_year &&
              form.topics &&
              form.topics.length > 0) && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: questionCounts.total > 0 ? 'divider' : 'error.main',
                    bgcolor: (t) =>
                      questionCounts.total > 0
                        ? t.palette.mode === 'light'
                          ? 'grey.50'
                          : 'action.hover'
                        : 'error.light',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: questionCounts.total > 0 ? 'text.primary' : 'error.main',
                    }}
                  >
                    {questionCounts.total > 0 ? 'Available questions' : 'No questions available'}
                  </Typography>
                  {questionCounts.total > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      {showMyQuestions
                        ? myQuestions
                          ? 'Showing questions created by you only.'
                          : 'Showing all available questions in the system.'
                        : 'Showing all available questions in the system.'}
                    </Typography>
                  )}
                  {questionCounts.total > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label={`Total: ${questionCounts.total}`} color="primary" variant="outlined" />
                      <Chip label={`Basic: ${questionCounts.basic}`} color="success" variant="outlined" />
                      <Chip
                        label={`Advanced: ${questionCounts.advanced}`}
                        color="warning"
                        variant="outlined"
                      />
                      {isLoadingCounts && (
                        <Chip label="Loading…" color="default" variant="outlined" />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="error.main">
                      No questions found for the selected criteria. Please try different subject, board,
                      year, or topics.
                    </Typography>
                  )}
                  {questionCounts.total > 0 &&
                    form.num_questions > 0 &&
                    (() => {
                      const requestedBasic = Math.round((form.percent_basic / 100) * form.num_questions);
                      const requestedAdvanced = Math.round(
                        (form.percent_advanced / 100) * form.num_questions
                      );
                      const errors = [];
                      if (requestedBasic > 0 && requestedBasic > questionCounts.basic) {
                        errors.push(
                          `Requested ${requestedBasic} Basic questions but only ${questionCounts.basic} available`
                        );
                      }
                      if (requestedAdvanced > 0 && requestedAdvanced > questionCounts.advanced) {
                        errors.push(
                          `Requested ${requestedAdvanced} Advanced questions but only ${questionCounts.advanced} available`
                        );
                      }
                      return errors.length > 0 ? (
                        <Box sx={{ mt: 2 }}>
                          {errors.map((error, index) => (
                            <Alert key={index} severity="error" sx={{ mb: 1 }}>
                              {error}
                            </Alert>
                          ))}
                        </Box>
                      ) : null;
                    })()}
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      </DialogContent>
      <DialogActions
        sx={{
          flexShrink: 0,
          p: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canGenerate || submitting}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {submitting ? 'Creating...' : saveButtonText}
        </Button>
      </DialogActions>
      
      {/* Local Snackbar for validation errors */}
      <Snackbar 
        open={showLocalSnackbar} 
        autoHideDuration={4000} 
        onClose={() => setShowLocalSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert severity="error" onClose={() => setShowLocalSnackbar(false)}>{localError}</Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AutoTestDialog;
